/**
 * Trzy zakładki portalu: paski płacowe, rozliczenia i rozwój (spec 03 §6, §7, §9).
 *
 * Paski są realne od chwili uruchomienia silnika naliczeń (P5): pokazują kwoty
 * zapisane przy naliczeniu i status „Wstępna" albo „Wypłacona" wg stanu listy
 * (spec 03 §380). Rozwój pokazuje WŁASNE pozycje z modułu rozwoju — budżet
 * roczny jest firmowy, więc portal go nie pokazuje. Saldo PPK nadal nie ma
 * źródła i mówi to wprost: wpłaty znamy z własnych naliczeń, salda nie [D19].
 */
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Bank,
  Button,
  Card,
  Document,
  EmptyState,
  GraduationCap,
  ProgressBar,
  Spinner,
  StatCard,
  StatusPill,
  Wallet,
  useToast,
} from '@k1p4/ui'
import { formatDate, formatPLN } from '@k1p4/i18n'
import { ApiError, api, apiToday, openFile } from '../../api/client'
import styles from './portal.module.css'
import { useFormat } from '../../format'

export function PortalPay() {
  const fmt = useFormat()
  const { t } = useTranslation()
  const pay = useQuery({ queryKey: ['me', 'pay'], queryFn: () => api.mePay() })

  if (pay.isPending) return <Spinner label={t('common.loading')} />
  if (pay.isError) return <EmptyState title={t('common.error')} description={t('common.retry')} />

  const items = pay.data.items

  return (
    <>
      <p className={styles.lead}>{t('portal.pay.lead')}</p>

      {items.length === 0 ? (
        <EmptyState
          icon={<Wallet size={28} />}
          title={t('portal.pay.empty')}
          description={t('portal.pay.emptyBody')}
        />
      ) : (
        <Card pad="flush" className={styles.list}>
          {items.map((item) => (
            <div key={item.id} className={styles.row}>
              <span className={styles.rowIcon} aria-hidden="true">
                <Wallet size={18} />
              </span>
              <div className={styles.rowBody}>
                <p className={styles.rowTitle}>
                  {t(item.contractType === 'b2b' ? 'portal.pay.periodB2b' : 'portal.pay.period', {
                    period: item.month.split('-').toReversed().join('/'),
                  })}
                </p>
                <p className={styles.rowMeta}>
                  {/* Po potrąceniach na rachunek idzie mniej niż netto (fala 9b, grupa 20; D108). */}
                  {t((item.payoutGr ?? item.netGr) === item.netGr ? 'portal.pay.amounts' : 'portal.pay.amountsPayout', {
                    gross: formatPLN(item.grossGr),
                    net: formatPLN(item.netGr),
                    payout: formatPLN(item.payoutGr ?? item.netGr),
                  })}
                </p>
                {item.sickPayGr > 0 ? (
                  <p className={styles.rowNote}>
                    {t('portal.leaves.sickPayNote', {
                      amount: formatPLN(item.sickPayGr),
                      count: item.sickDays,
                    })}
                  </p>
                ) : null}
                {item.benefitGr > 0 ? (
                  <p className={styles.rowNote}>
                    {t('portal.leaves.benefitNote', { amount: formatPLN(item.benefitGr) })}
                  </p>
                ) : null}
                {item.payoutDate ? (
                  <p className={styles.rowNote}>
                    {t('portal.pay.payout', { date: fmt.date(item.payoutDate, 'long') })}
                  </p>
                ) : null}
              </div>
              <div className={styles.rowActions}>
                <StatusPill tone={item.status === 'paid' ? 'success' : 'warning'}>
                  {t(`portal.pay.status.${item.status}`)}
                </StatusPill>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    openFile(api.myPayslipPdfUrl(item.id))
                  }}
                >
                  {t('portal.pay.download')}
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}
    </>
  )
}

export function PortalTax() {
  const { t } = useTranslation()
  const toast = useToast()
  const tax = useQuery({ queryKey: ['me', 'tax'], queryFn: () => api.meTax() })
  /* Pobranie jak w zakładce Dokumenty: fetch, PDF z treści na żądanie, błąd zdaniem (grupa 48). */
  const download = useMutation({
    mutationFn: (item: { id: string; title: string }) =>
      api.downloadMyDocumentFile(item.id, `${item.title}.pdf`),
    onError: (caught) => {
      const code = caught instanceof ApiError ? caught.code : null
      toast.show(
        t(
          code === 'PDF_UNAVAILABLE'
            ? 'portal.documents.pdfUnavailable'
            : code === 'FILE_MISSING'
              ? 'portal.documents.noContent'
              : 'portal.documents.downloadFailed',
        ),
        { tone: 'danger' },
      )
    },
  })

  if (tax.isPending) return <Spinner label={t('common.loading')} />
  if (tax.isError) return <EmptyState title={t('common.error')} description={t('common.retry')} />

  const items = tax.data.items

  return (
    <>
      <p className={styles.lead}>{t('portal.tax.lead')}</p>

      {/* Własna sekcja daje odstęp przed nagłówkiem PPK (#98). */}
      <section className={styles.section}>
        {items.length === 0 ? (
          <EmptyState
            icon={<Bank size={28} />}
            title={t('portal.tax.empty')}
            description={t('portal.tax.emptyBody')}
          />
        ) : (
          <Card pad="flush" className={styles.list}>
            {items.map((item) => (
              <div key={item.id} className={styles.row}>
                <span className={styles.rowIcon} aria-hidden="true">
                  <Document size={18} />
                </span>
                <div className={styles.rowBody}>
                  <p className={styles.rowTitle}>{item.title}</p>
                  {item.date ? (
                    <p className={styles.rowMeta}>{formatDate(item.date, 'dot')}</p>
                  ) : null}
                </div>
                {/* Pobranie jak w zakładce Dokumenty — makieta spec 03 §7 ma tu akcję. */}
                {item.fileId !== null || item.hasContent ? (
                  /* Obszar akcji nie kurczy się na telefonie — jak w zakładce wypłat. */
                  <div className={styles.rowActions}>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={download.isPending}
                      onClick={() => download.mutate(item)}
                    >
                      {t('portal.tax.download')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </Card>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('portal.tax.ppkTitle')}</h2>
        {/*
          Cztery stany (grupa 169): wpłaty są — kafle; umowa bez PPK — „nie
          dotyczy"; rezygnacja — zdanie o deklaracji z art. 23 ustawy o PPK;
          reszta — brak wpłat w roku, bez sugestii, że rachunek czeka na podpięcie.
        */}
        {tax.data.ppkContributions === null ? (
          tax.data.ppkCovered === false ? (
            /* Umowa bez uczestnictwa w PPK — nie „brak wpłat", który sugeruje, że przyjdą. */
            <EmptyState
              title={t('portal.tax.ppkNotCovered')}
              description={t('portal.tax.ppkNotCoveredBody')}
            />
          ) : tax.data.ppkStatus === 'rezygnacja' ? (
            <EmptyState
              title={t('portal.tax.ppkResigned')}
              description={t('portal.tax.ppkResignedBody')}
            />
          ) : (
            <EmptyState
              title={t('portal.tax.ppkNoContributions', {
                year: tax.data.ppkYear ?? apiToday().getFullYear(),
              })}
              description={t('portal.tax.ppkNoContributionsBody')}
            />
          )
        ) : (
          <>
            <div className={styles.kpiGrid}>
              <StatCard
                label={t('portal.tax.ppkEmployee')}
                value={formatPLN(tax.data.ppkContributions.employeeGr)}
                note={t('portal.tax.ppkYear', { year: tax.data.ppkContributions.year })}
              />
              <StatCard
                label={t('portal.tax.ppkEmployer')}
                value={formatPLN(tax.data.ppkContributions.employerGr)}
                note={t('portal.tax.ppkYear', { year: tax.data.ppkContributions.year })}
              />
            </div>
            {/* Saldo inwestycji prowadzi instytucja finansowa — my znamy wpłaty. */}
            <p className={styles.formNote}>{t('portal.tax.ppkBalanceNote')}</p>
          </>
        )}
      </section>
    </>
  )
}

export function PortalGrowth() {
  const { t } = useTranslation()
  const growth = useQuery({ queryKey: ['me', 'growth'], queryFn: () => api.meGrowth() })

  if (growth.isPending) return <Spinner label={t('common.loading')} />
  if (growth.isError) return <EmptyState title={t('common.error')} description={t('common.retry')} />

  const items = growth.data.items

  return (
    <>
      <p className={styles.lead}>{t('portal.growth.lead')}</p>

      {items.length === 0 ? (
        <EmptyState
          icon={<GraduationCap size={28} />}
          title={t('portal.growth.empty')}
          description={t('portal.growth.emptyBody')}
        />
      ) : (
        <Card pad="flush" className={styles.list}>
          {items.map((item) => (
            <div key={item.id} className={styles.row}>
              <span className={styles.rowIcon} aria-hidden="true">
                <GraduationCap size={18} />
              </span>
              <div className={styles.rowBody}>
                <p className={styles.rowTitle}>{item.title}</p>
                <p className={styles.rowMeta}>
                  {[
                    item.form,
                    item.dueDate ? formatDate(item.dueDate, 'dot') : null,
                    item.costGr === null ? null : formatPLN(item.costGr),
                  ]
                    .filter((part) => part !== null && part !== '')
                    .join(' · ')}
                </p>
                {/*
                  Makieta pokazuje postęp PASKIEM, nie liczbą w wierszu meta
                  (tor 8 px, wypełnienie brandowe). Pasek niesie wartość dla
                  czytników ekranu przez aria-valuenow, więc tekst „postęp X %"
                  nie jest już potrzebny.
                */}
                {item.progressPct === null ? null : (
                  <ProgressBar
                    value={item.progressPct}
                    max={100}
                    label={t('portal.growth.progress', { percent: item.progressPct })}
                    className={styles.growthBar}
                  />
                )}
              </div>
              {/*
                Etykiety statusów są już w hrDevelopment.status — portal używa
                tych samych, zamiast dublować słownik dla drugiej strony ekranu.
              */}
              <StatusPill tone={item.status === 'ukonczone' ? 'success' : 'neutral'}>
                {t(`hrDevelopment.status.${item.status}`)}
              </StatusPill>
            </div>
          ))}
        </Card>
      )}
    </>
  )
}
